import { useState } from "react";
import { motion } from "framer-motion";
import { Phone, Mail, MapPin, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { submitContact } from "../lib/api";

const services = [
  "General Building Maintenance",
  "Plumbing & Drainage",
  "Electrical Maintenance",
  "Security & Access Control",
  "Landscaping & Groundskeeping",
  "Preventative Maintenance Plan",
  "Emergency Call-Out",
  "Other / Not sure",
];

export const Contact = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    service: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in name, email, and message.");
      return;
    }
    setSubmitting(true);
    try {
      await submitContact(form);
      toast.success("Inquiry received. We'll be in touch shortly.");
      setForm({ name: "", email: "", phone: "", company: "", service: "", message: "" });
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong. Please try again or call us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      className="relative py-24 md:py-36 bg-cream"
      data-testid="contact-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12">
        {/* Left */}
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="lg:col-span-5"
        >
          <div className="uppercase text-xs tracking-[0.3em] text-sage mb-6">
            Request a quote
          </div>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-forest leading-[1.05] tracking-tight">
            Let's talk about
            <br />
            <span className="italic">your estate.</span>
          </h2>
          <p className="text-graphite text-base md:text-lg leading-relaxed mt-8 max-w-md">
            Share a few details about your property and the services you need. A
            Greenroute specialist will respond within one business day.
          </p>

          <div className="divider-line my-12" />

          <div className="space-y-6">
            <a
              href="tel:+27661833893"
              className="flex items-start gap-4 group"
              data-testid="contact-phone"
            >
              <div className="w-11 h-11 bg-forest flex items-center justify-center shrink-0">
                <Phone size={18} className="text-cream" />
              </div>
              <div>
                <div className="uppercase text-[10px] tracking-[0.25em] text-sage">
                  Call us
                </div>
                <div className="font-serif text-2xl text-ink group-hover:text-forest transition-colors">
                  +27 66 183 3893
                </div>
              </div>
            </a>

            <a
              href="mailto:admin@green-route.co.za"
              className="flex items-start gap-4 group"
              data-testid="contact-email"
            >
              <div className="w-11 h-11 bg-forest flex items-center justify-center shrink-0">
                <Mail size={18} className="text-cream" />
              </div>
              <div>
                <div className="uppercase text-[10px] tracking-[0.25em] text-sage">
                  Email
                </div>
                <div className="font-serif text-2xl text-ink group-hover:text-forest transition-colors">
                  admin@green-route.co.za
                </div>
              </div>
            </a>

            <div className="flex items-start gap-4" data-testid="contact-address">
              <div className="w-11 h-11 bg-forest flex items-center justify-center shrink-0">
                <MapPin size={18} className="text-cream" />
              </div>
              <div>
                <div className="uppercase text-[10px] tracking-[0.25em] text-sage">
                  Registered Office
                </div>
                <div className="font-serif text-xl text-ink leading-snug max-w-xs">
                  Cnr John & Earp Street, Selby, Johannesburg, 2001
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Form */}
        <motion.form
          initial={{ opacity: 0, x: 30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          onSubmit={onSubmit}
          className="lg:col-span-7 bg-white border border-bordersoft p-8 md:p-12"
          data-testid="contact-form"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Field label="Full name *" value={form.name} onChange={update("name")} testId="contact-name" />
            <Field label="Email *" type="email" value={form.email} onChange={update("email")} testId="contact-email-input" />
            <Field label="Phone" type="tel" value={form.phone} onChange={update("phone")} testId="contact-phone-input" />
            <Field label="Company / Body Corporate" value={form.company} onChange={update("company")} testId="contact-company-input" />
          </div>

          <div className="mt-6">
            <label className="uppercase text-[10px] tracking-[0.25em] text-sage mb-2 block">
              Service interest
            </label>
            <select
              value={form.service}
              onChange={update("service")}
              data-testid="contact-service-select"
              className="w-full bg-cream border border-bordersoft px-4 py-3.5 text-ink focus:outline-none focus:border-forest transition"
            >
              <option value="">Select a service</option>
              {services.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>

          <div className="mt-6">
            <label className="uppercase text-[10px] tracking-[0.25em] text-sage mb-2 block">
              Tell us about your property & needs *
            </label>
            <textarea
              rows={5}
              value={form.message}
              onChange={update("message")}
              data-testid="contact-message-input"
              className="w-full bg-cream border border-bordersoft px-4 py-3.5 text-ink focus:outline-none focus:border-forest transition resize-none"
              placeholder="Number of units, current concerns, urgency, etc."
            />
          </div>

          <button
            type="submit"
            disabled={submitting}
            data-testid="contact-submit-btn"
            className="mt-8 inline-flex items-center gap-3 bg-forest text-cream px-7 py-4 text-sm tracking-wider hover:bg-forest/90 transition-colors duration-300 disabled:opacity-60"
          >
            {submitting ? "Submitting…" : "Send Inquiry"}
            <ArrowRight size={18} />
          </button>
          <p className="mt-4 text-xs text-graphite/70">
            We respect your privacy. Your details are used solely to respond to your inquiry.
          </p>
        </motion.form>
      </div>
    </section>
  );
};

const Field = ({ label, type = "text", value, onChange, testId }) => (
  <div>
    <label className="uppercase text-[10px] tracking-[0.25em] text-sage mb-2 block">
      {label}
    </label>
    <input
      type={type}
      value={value}
      onChange={onChange}
      data-testid={testId}
      className="w-full bg-cream border border-bordersoft px-4 py-3.5 text-ink focus:outline-none focus:border-forest transition"
    />
  </div>
);

export default Contact;
